// src/components/EnquiryRedeemed.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import "./EnquiryResults.css";
import useEscapeToGoBack from "../hooks/useEscapeToGoBack";

const API_LIST   = "https://www.lianfoong.com/api/pawn-transactions/redeemed/by-mmyy";
const API_REPORT = "https://www.lianfoong.com/api/pawn-transactions/redeemed/by-mmyy/report";

const fmt = (s) => {
  if (!s) return "-";
  const z = String(s).split("T")[0].split("-");
  return z.length === 3 ? `${z[2]}/${z[1]}/${z[0]}` : s;
};
const money = (n) => Number(n || 0).toFixed(2);

export default function EnquiryRedeemed() {
  useEscapeToGoBack();

  const [mmyy, setMMYY] = useState("");
  const [rows, setRows] = useState(null);
  const [err, setErr]   = useState("");
  const [starOnly, setStarOnly] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); inputRef.current?.select?.(); }, []);

  const onSearch = async () => {
    const v = mmyy.replace(/\D/g, "").slice(0,4);
    if (!/^\d{4}$/.test(v)) { setErr("Enter MMYY, e.g. 0425"); return; }
    try {
      setErr(""); setRows(null);
      const r = await axios.get(API_LIST, { params: { mmyy: v } }); // server returns all; we filter client-side too
      setRows(r.data || []);
    } catch (e) {
      setErr(e?.response?.data || "Failed to load");
      setRows([]);
    }
  };

  const onPrint = () => {
    const v = mmyy.replace(/\D/g, "").slice(0,4);
    if (!/^\d{4}$/.test(v)) { alert("Enter MMYY, e.g. 0425"); return; }
    // server will apply the same filter so PDF matches screen
    window.open(`${API_REPORT}?mmyy=${encodeURIComponent(v)}&starOnly=${starOnly?1:0}`, "_blank");
  };

  // Screen filter (so user sees it immediately)
  const filtered = useMemo(() => {
    if (!rows) return null;
    return starOnly ? rows.filter(r => /\*/.test(String(r.item||""))) : rows;
  }, [rows, starOnly]);

  const totals = useMemo(() => {
    if (!filtered) return { count: 0, grant: 0 };
    return {
      count: filtered.length,
      grant: filtered.reduce((a,r) => a + Number(r.amount||0), 0) // sum of GRANT amount
    };
  }, [filtered]);

  return (
    <div className="enquiryResults">
      <div className="row space-between">
        <h2>Redeemed — Search by MMYY</h2>
        <div className="row" style={{gap:12}}>
          <input
            ref={inputRef}
            className="input"
            placeholder="e.g. 0425"
            inputMode="numeric"
            maxLength={4}
            value={mmyy}
            onChange={(e)=>setMMYY(e.target.value.replace(/\D/g,"").slice(0,4))}
            onKeyDown={(e)=>e.key==="Enter" && onSearch()}
            style={{minWidth:120}}
          />
          <button className="btn primary" onClick={onSearch}>Search</button>
          <button
            className="btn secondary"
            onClick={()=>setStarOnly(x=>!x)}
            title="Show only items that contain an asterisk (*)"
          >
            {starOnly ? "Showing * only" : "Filter * items"}
          </button>
          <button className="btn secondary" onClick={onPrint}>Print</button>
        </div>
      </div>

      {err && <div className="alert error">{String(err)}</div>}

      {!filtered ? (
        <div style={{marginTop:12}}>Enter MMYY and press Search…</div>
      ) : filtered.length === 0 ? (
        <div className="empty">No redeemed tickets for {mmyy}{starOnly ? " (with *)" : ""}</div>
      ) : (
        <>
          <div className="stats" style={{marginTop:12}}>
            <div className="stat">Count: <b>{totals.count}</b></div>
            <div className="stat">Grant Amount: <b>${money(totals.grant)}</b></div>
            {starOnly && <div className="stat">Filter: <b>* items only</b></div>}
          </div>

          <div className="table-wrapper">
            <table className="table">
              <thead>
  <tr>
    <th>No</th>
    <th>Receipt</th>
    <th>Grant Date</th>
    <th>Close Date</th>
    <th className="xt-right">Amount</th>
    <th className="xt-right">Weight</th>
    <th>Item</th>
    <th>Expiry Date</th>

    {/* ✅ NEW */}
    <th>Redeem By (NRIC)</th>
    <th>Redeem Handler</th>
  </tr>
</thead>
              <tbody>
  {filtered.map((r, i) => (
    <tr key={r.receiptNo ?? i}>
      <td>{i+1}</td>
      <td>{r.receiptNo}</td>
      <td>{fmt(r.grantDate)}</td>
      <td>{fmt(r.redeemDate)}</td>
      <td className="xt-right">{money(r.amount)}</td>
      <td className="xt-right">{String(r.weight ?? "").trim()}</td>
      <td>{r.item}</td>
      <td>{fmt(r.expiryDate)}</td>

      {/* ✅ NEW (fallback to "-") */}
      <td>{(r.redeemBy && String(r.redeemBy).trim()) ? r.redeemBy : "-"}</td>
      <td>{(r.redeemHandler && String(r.redeemHandler).trim()) ? r.redeemHandler : "-"}</td>
    </tr>
  ))}
</tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
